import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Autocomplete,
  CircularProgress,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { POSITIONS } from '../../utils/constants';
import { alpha } from '@mui/material/styles';
import { API_ENDPOINTS } from '../../config/api';
import { useTranslation } from 'react-i18next';

const TeamScheduler = ({ team = [], onTeamChange, customFetch }) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    userId: '',
    name: '',
    position: '',
    customPosition: '',
    status: 'pending',
    declineReason: ''
  });

  const fetchUsers = async (withDelay = false) => {
    try {
      setLoading(true);
      
      if (withDelay) {
        await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay
      }
      
      const response = await customFetch.get(API_ENDPOINTS.USERS.BASE, {
        params: {
          roles: ['musician', 'leader', 'admin', 'member'],
          sort: 'firstName'
        }
      });
      if (response.data?.data) {
        setUsers(response.data.data);
        
        // If we're in edit mode, update the form data with the correct user
        if (editingIndex !== null && team[editingIndex]) {
          const member = team[editingIndex];
          setFormData(prev => ({
            ...prev,
            userId: member.user?._id || '',
            name: member.user ? `${member.user.firstName} ${member.user.lastName}` : '',
            position: member.position || '',
            customPosition: member.customPosition || '',
            status: member.status || 'pending',
            declineReason: member.declineReason || ''
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch when component mounts
  useEffect(() => {
    fetchUsers(false);
  }, []);

  // Fetch with delay when dialog opens
  useEffect(() => {
    if (dialogOpen) {
      fetchUsers(true);
    }
  }, [dialogOpen]);

  const handleClickOpen = () => {
    setDialogOpen(true);
    setFormData({
      userId: '',
      name: '',
      position: '',
      customPosition: '',
      status: 'pending',
      declineReason: ''
    });
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingIndex(null);
    setFormData({
      userId: '',
      name: '',
      position: '',
      customPosition: '',
      status: 'pending',
      declineReason: ''
    });
  };

  const handleEdit = (index) => {
    try {
      const member = team[index];
      if (!member || !member.user) {
        throw new Error('Invalid team member data');
      }

      setEditingIndex(index);
      setFormData({
        userId: member.user._id || '',
        name: `${member.user.firstName} ${member.user.lastName}`,
        position: member.position || '',
        customPosition: member.customPosition || '',
        status: member.status || 'pending',
        declineReason: member.declineReason || ''
      });
      setDialogOpen(true);
    } catch (error) {
      console.error('Failed to edit team member:', error);
    }
  };

  const handleDelete = (index) => {
    try {
      // Get the member being deleted for logging purposes
      const memberToDelete = team[index];
      console.log('Deleting team member:', memberToDelete);
      
      // Create a new team array without the deleted member
      const newTeam = team.filter((_, idx) => idx !== index);
      
      // Log the team before and after deletion for debugging
      console.log('Team before deletion:', team);
      console.log('Team after deletion:', newTeam);
      
      // Update the parent component with the new team
      onTeamChange(newTeam);
    } catch (error) {
      console.error('Failed to delete team member:', error);
    }
  };

  const handleSubmit = () => {
    try {
      const selectedUser = users.find(user => user._id === formData.userId);
      if (!selectedUser) {
        throw new Error('Selected user not found');
      }

      // Validate form data
      if (!formData.position) {
        throw new Error('Position is required');
      }

      if (formData.status === 'declined' && !formData.declineReason.trim()) {
        throw new Error('Decline reason is required when status is declined');
      }

      // If we're editing an existing member, prepare the member data for UI display
      if (editingIndex !== null && team[editingIndex]) {
        const memberData = {
          ...team[editingIndex],
          position: formData.position,
          customPosition: formData.position === 'Other' ? formData.customPosition : '',
          status: formData.status,
          declineReason: formData.status === 'declined' ? formData.declineReason.trim() : ''
        };

        // Update team locally
        const newTeam = team.map((member, index) => 
          index === editingIndex ? memberData : member
        );

        // Update parent component
        onTeamChange(newTeam);
        handleClose();
      } else {
        // For new members, prepare data for API
        const memberData = {
          user: selectedUser._id, // Just the ID for the API
          position: formData.position,
          customPosition: formData.position === 'Other' ? formData.customPosition : '',
          status: formData.status,
          declineReason: formData.status === 'declined' ? formData.declineReason.trim() : '',
          role: 'musician'
        };

        // Add the member to the UI with the full user object for display
        const tempMember = {
          user: selectedUser, // Keep full user object for UI
          position: formData.position,
          customPosition: formData.position === 'Other' ? formData.customPosition : '',
          status: formData.status,
          declineReason: formData.status === 'declined' ? formData.declineReason.trim() : '',
          role: 'musician'
        };

        const newTeam = [...team, tempMember];
        onTeamChange(newTeam, memberData);
        handleClose();
      }
    } catch (error) {
      console.error('Failed to submit team member:', error);
      // You might want to show this error to the user via a toast or alert
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'declined':
        return 'error';
      default:
        return 'default';
    }
  };

  const renderDialog = () => {
    // When editing, only disable if it's a custom position without a value
    const isDisabled = loading || 
      (editingIndex === null 
        ? (!formData.userId || !formData.position) // New member validation
        : (formData.position === 'Other' && !formData.customPosition) // Edit mode validation
      ) || (formData.status === 'declined' && !formData.declineReason.trim());

    console.log('Dialog render state:', {
      loading,
      users: users.length,
      formData,
      editingIndex
    });

    return (
      <Dialog 
        open={dialogOpen} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        TransitionProps={{
          onEntered: () => {
            // Force a re-render after the dialog has entered
            setFormData(prev => ({...prev}));
          }
        }}
      >
        <DialogTitle>
          {editingIndex !== null ? t('services.teamSection.editMember') : t('services.teamSection.addMember')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              key={`user-select-${dialogOpen}-${loading}`}
              value={users.find(user => user._id === formData.userId) || null}
              onChange={(event, newValue) => {
                console.log('User selected:', newValue);
                setFormData(prev => ({
                  ...prev,
                  userId: newValue?._id || '',
                  name: newValue ? `${newValue.firstName} ${newValue.lastName}` : ''
                }));
              }}
              options={users}
              loading={loading}
              getOptionLabel={(option) => 
                option ? `${option.firstName} ${option.lastName}${option.instruments?.length ? ` (${option.instruments.join(', ')})` : ''}` : ''
              }
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1">
                      {option.firstName} {option.lastName}
                    </Typography>
                    {option.instruments?.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {option.instruments.join(', ')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('services.teamSection.selectMember')}
                  fullWidth
                  margin="normal"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option?._id === value?._id}
              noOptionsText={loading ? t('common.loading') : t('services.teamSection.noUsersFound')}
              disabled={loading}
            />

            <FormControl fullWidth margin="normal">
              <Autocomplete
                value={formData.position}
                onChange={(event, newValue) => {
                  console.log('Position selected:', newValue);
                  setFormData(prev => ({
                    ...prev,
                    position: newValue || ''
                  }));
                }}
                options={[...Object.values(POSITIONS), 'Other']}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('services.teamSection.position')}
                    fullWidth
                  />
                )}
                disabled={loading}
              />
            </FormControl>

            {formData.position === 'Other' && (
              <TextField
                fullWidth
                margin="normal"
                label={t('services.teamSection.customPosition')}
                value={formData.customPosition}
                onChange={(e) => {
                  console.log('Custom position changed:', e.target.value);
                  setFormData(prev => ({
                    ...prev,
                    customPosition: e.target.value
                  }));
                }}
              />
            )}

            <FormControl fullWidth margin="normal">
              <InputLabel id="status-label">{t('services.teamSection.status')}</InputLabel>
              <Select
                labelId="status-label"
                value={formData.status}
                onChange={(e) => {
                  console.log('Status selected:', e.target.value);
                  setFormData(prev => ({
                    ...prev,
                    status: e.target.value,
                    // Clear decline reason if status is not declined
                    declineReason: e.target.value !== 'declined' ? '' : prev.declineReason
                  }));
                }}
                label={t('services.teamSection.status')}
              >
                <MenuItem value="pending">{t('services.status.pending')}</MenuItem>
                <MenuItem value="confirmed">{t('services.status.confirmed')}</MenuItem>
                <MenuItem value="declined">{t('services.status.declined')}</MenuItem>
              </Select>
            </FormControl>

            {formData.status === 'declined' && (
              <TextField
                fullWidth
                margin="normal"
                label={t('services.teamSection.declineReason')}
                required
                value={formData.declineReason}
                onChange={(e) => {
                  console.log('Decline reason changed:', e.target.value);
                  setFormData(prev => ({
                    ...prev,
                    declineReason: e.target.value
                  }));
                }}
                error={formData.status === 'declined' && !formData.declineReason.trim()}
                helperText={formData.status === 'declined' && !formData.declineReason.trim() ? t('services.teamSection.declineReasonRequired') : ''}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('common.cancel')}</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={isDisabled}
          >
            {editingIndex !== null ? t('common.update') : t('common.add')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">{t('services.teamSection.title')}</Typography>
        <Button
          startIcon={<AddIcon />}
          onClick={handleClickOpen}
          variant="outlined"
          size="small"
        >
          {t('services.teamSection.addMember')}
        </Button>
      </Box>

      {team.length > 0 ? (
        <Paper 
          elevation={0}
          sx={{ 
            p: 2, 
            bgcolor: alpha('#1976d2', 0.04),
            borderRadius: 2
          }}
        >
          {team.map((member, index) => (
            <Box 
              key={index}
              sx={{ 
                py: 1,
                '&:not(:last-child)': {
                  borderBottom: 1,
                  borderColor: 'divider'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body1">
                    {member.user ? `${member.user.firstName} ${member.user.lastName}` : 'Unknown User'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {member.position}
                    {member.customPosition && ` - ${member.customPosition}`}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={member.status}
                    size="small"
                    color={getStatusColor(member.status)}
                    sx={{ 
                      textTransform: 'capitalize',
                      ...(member.status === 'pending' && {
                        bgcolor: '#FFF3BF',
                        color: '#997404',
                        '& .MuiChip-label': {
                          color: '#997404'
                        }
                      })
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(index)}
                    sx={{ 
                      color: '#a259ff',
                      '&:hover': {
                        bgcolor: alpha('#a259ff', 0.04)
                      }
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(index)}
                    sx={{ 
                      color: '#ff5252',
                      '&:hover': {
                        bgcolor: alpha('#ff5252', 0.04)
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          ))}
        </Paper>
      ) : (
        <Typography variant="body2" color="text.secondary">{t('services.teamSection.noMembers')}</Typography>
      )}

      {renderDialog()}
    </Box>
  );
};

export default TeamScheduler;
